import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DEFAULT_LANGUAGE, FALLBACK_LANGUAGE, findLanguage, Language, LanguageCode, SUPPORTED_LANGUAGES } from '../i18n/languages';
import { TRANSLATION_LOADERS } from '../i18n/translation-loaders';
import fr from '../i18n/translations/fr';
import { AccountService } from './account.service';

const STORAGE_KEY = 'language';
type Params = { [name: string]: string | number | null | undefined };

// Langue de l'interface : celle de l'appareil par défaut, sinon celle choisie dans Paramètres → Préférences
@Injectable({
  providedIn: 'root'
})
export class I18nService {

  readonly languages = SUPPORTED_LANGUAGES;

  private language = new BehaviorSubject<LanguageCode>(DEFAULT_LANGUAGE);
  language$ = this.language.asObservable();

  private readonly reference = flatten(fr);
  private dictionary = this.reference;
  // Choix explicite de l'utilisateur ; null = suivre la langue de l'appareil
  private choice: LanguageCode | null = null;
  private pending = 0;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    accountService: AccountService
  ) {
    // Langue enregistrée sur le compte : retrouvée d'un appareil à l'autre
    accountService.account$.subscribe(account => {
      const saved = findLanguage(account?.preferences?.['language'] as string);
      if (saved && saved.code !== this.choice) {
        this.setLanguage(saved.code);
      }
    });
  }

  // Appelé avant le premier rendu (provideAppInitializer) pour éviter un flash dans la mauvaise langue
  init(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }
    this.choice = findLanguage(this.readStorage())?.code || null;
    return this.apply(this.choice || this.deviceLanguage);
  }

  get current(): LanguageCode {
    return this.language.value;
  }

  get userChoice(): LanguageCode | null {
    return this.choice;
  }

  get deviceLanguage(): LanguageCode {
    if (!isPlatformBrowser(this.platformId)) {
      return DEFAULT_LANGUAGE;
    }
    const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const candidate of candidates) {
      const language = findLanguage(candidate);
      if (language) {
        return language.code;
      }
    }
    return FALLBACK_LANGUAGE;
  }

  languageInfo(code: LanguageCode): Language {
    return this.languages.find(l => l.code === code) || this.languages[0];
  }

  // null = revenir à la langue de l'appareil
  setLanguage(code: LanguageCode | null): Promise<void> {
    this.choice = code;
    this.writeStorage(code);
    return this.apply(code || this.deviceLanguage);
  }

  t(key: string, params?: Params): string {
    let text = this.dictionary.get(key) ?? this.reference.get(key) ?? key;
    if (params) {
      for (const name of Object.keys(params)) {
        text = text.split('{{' + name + '}}').join(String(params[name] ?? ''));
      }
    }
    return text;
  }

  private async apply(code: LanguageCode): Promise<void> {
    const request = ++this.pending;
    let dictionary = this.reference;
    if (code !== 'fr') {
      try {
        dictionary = flatten(await TRANSLATION_LOADERS[code]());
      } catch (err) {
        console.error('Chargement de la langue impossible : ' + code, err);
        return;
      }
    }
    // Un choix plus récent a pu arriver pendant le chargement
    if (request !== this.pending) {
      return;
    }
    this.dictionary = dictionary;
    const root = this.document.documentElement;
    root.lang = code;
    root.dir = this.languageInfo(code).dir;
    this.language.next(code);
  }

  private readStorage(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private writeStorage(code: LanguageCode | null): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      if (code) {
        localStorage.setItem(STORAGE_KEY, code);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Stockage indisponible (navigation privée…) : le choix ne vaut que pour la session
    }
  }
}

function flatten(tree: object, prefix = '', into = new Map<string, string>()): Map<string, string> {
  for (const [key, value] of Object.entries(tree)) {
    const path = prefix ? prefix + '.' + key : key;
    if (typeof value === 'string') {
      into.set(path, value);
    } else if (value && typeof value === 'object') {
      flatten(value, path, into);
    }
  }
  return into;
}
